import React, { useState, useContext } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Navbar from '../../../Shared/Navbar/Navbar';
import { baseURL, headofSalesMail } from '../../../Apiservices/Api';
import DataTable from "../../../Layout/Table/TableLayout";
import { AuthContext } from '../../../AuthContext/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const SupplierApproval = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userEmail } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState("payables");

  /* =========================
     FETCH PAYMENT LOGS
  ========================== */

  const {
    data: paymentLogs = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['supplier-payment-logs'],
    queryFn: async () => {
      const response = await axios.get(
        `${baseURL}/api/payment-logs-with-details`
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  /* =========================
     UPDATE STATUS
  ========================== */

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }) => {
      return axios.put(
        `${baseURL}/api/payment-logs/${id}/supplierstatus`,
        {
          status: newStatus,
          approved_by:
            userEmail === headofSalesMail
              ? "Head of Sales"
              : "Admin",
        }
      );
    },
    onSuccess: () => {
      alert("Status updated successfully");
      queryClient.invalidateQueries(['supplier-payment-logs']);
    },
    onError: (err) => {
      alert(
        `Failed to update status: ${
          err.response?.data?.message || err.message
        }`
      );
    },
  });

  const handleStatusChange = (id, newStatus) => {
    updateStatusMutation.mutate({ id, newStatus });
  };

  /* =========================
     DELETE PAYMENT
  ========================== */

  const deleteMutation = useMutation({
    mutationFn: async (paymentId) => {
      return fetch(`${baseURL}/api/supplier-payments/${paymentId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      alert("Payment deleted successfully");
      queryClient.invalidateQueries(['supplier-payment-logs']);
    },
    onError: (err) => {
      alert("Error deleting payment: " + err.message);
    },
  });

  const handleDelete = (paymentId) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) {
      return;
    }
    deleteMutation.mutate(paymentId);
  };

  /* =========================
     TABLE COLUMNS
  ========================== */

  const paymentLogColumns = [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
    },
    { Header: "Lead ID", accessor: "leadid" },
    { Header: "Customer Name", accessor: "customer_name" },
    { Header: "Supplier Name", accessor: "supplier_name" },
    {
      Header: "Paid On",
      accessor: "paid_on",
      Cell: ({ row }) => {
        if (!row.original.paid_on) return "-";
        const date = new Date(row.original.paid_on);
        return `${String(date.getDate()).padStart(2, "0")}/${String(
          date.getMonth() + 1
        ).padStart(2, "0")}/${date.getFullYear()}`;
      },
    },
    {
      Header: "Paid Amount",
      accessor: "paid_amount",
      Cell: ({ row }) =>
        row.original.paid_amount
          ? `₹${row.original.paid_amount}`
          : "-",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => (
        <select
          value={row.original.status || ""}
          onChange={(e) =>
            handleStatusChange(row.original.id, e.target.value)
          }
          style={{
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #ddd",
            backgroundColor:
              row.original.status === "Approved"
                ? "#e6f7e6"
                : row.original.status === "Rejected"
                ? "#ffebeb"
                : "#fff",
          }}
        >
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      ),
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <FaEdit
            style={{ color: "#ff9966", cursor: "pointer" }}
            onClick={() =>
              navigate(`/a-edit-sup-approval-payment/${row.original.id}`)
            }
          />
          <FaTrash
            style={{ color: "#ff9966", cursor: "pointer" }}
            onClick={() => handleDelete(row.original.id)}
          />
        </div>
      ),
    },
  ];

  /* =========================
     RENDER
  ========================== */

  return (
    <div className="salesViewLeadsContainer">
      <Navbar />
      <div className="salesViewLeads">
        <div className="payments-tabs">
          <button
            className={activeTab === "payables" ? "active" : ""}
            onClick={() => setActiveTab("payables")}
          >
            Payables
          </button>
          <button
            className={activeTab === "receivables" ? "active" : ""}
            onClick={() => {
              setActiveTab("receivables");
              navigate(`/a-approvals`);
            }}
          >
            Receivables
          </button>
        </div>

        <div style={{ marginTop: "30px" }}>
          <h3>Supplier Approvals</h3>

          {isLoading ? (
            <div>Loading...</div>
          ) : isError ? (
            <div>Error loading data</div>
          ) : (
            <DataTable
              columns={paymentLogColumns}
              data={paymentLogs}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierApproval;